import { Component, OnDestroy, OnInit, PLATFORM_ID, Renderer2, DOCUMENT, inject } from '@angular/core';
import {
  ApiService,
  AuthService,
  DocumentService,
  ImageService,
  PageService,
  PageType,
  PostService,
  PostType,
  SettingsService,
  WindowService,
} from '@gallaghercp/core-frontend';
import { ClientService } from '../../services/client.service';
import { Observable, Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { APP_CONFIG } from '@core-app/app.config';
import { SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'core-header',
  templateUrl: './header.component.html',
  standalone: false,
  host: {
    '(window:scroll)': 'onScroll()',
  },
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected auth = inject(AuthService);
  protected pages = inject(PageService);
  protected posts = inject(PostService);
  client = inject(ClientService);
  settings = inject(SettingsService);
  protected breakpoint = inject(BreakpointObserver);
  protected router = inject(Router);
  protected renderer = inject(Renderer2);
  protected api = inject(ApiService);
  protected imageService = inject(ImageService);
  protected windowService = inject(WindowService);
  protected documentService = inject(DocumentService);
  protected platformId = inject(PLATFORM_ID);
  protected document? = inject<Document>(DOCUMENT);

  user: any;
  isLoggedIn = false;

  pages$?: Observable<any>;
  page?: PageType;
  post?: PostType;

  openChildNav: any;
  hamburger = false;
  hamburgerChildNav = false;
  authenticationType?: string;
  isMobileNav = false;
  isBrowser!: boolean;

  isTopNav$?: Observable<boolean>;
  isTransparentNav?: boolean;
  isTopNavTransparentSticky?: boolean;
  isIndividualMenuFlyout?: boolean = true;
  menuFlyoutPageId: number | undefined;

  subscriptions: Subscription[] = [];

  // Config variables
  darkMode?: boolean;
  postsInNavEnabled?: boolean;
  postsEnabled?: boolean;
  notificationsEnabled?: boolean;
  searchEnabled?: boolean;
  heroImagesEnabled?: boolean;
  isCollapsed = true;
  isDemoMode = false;

  isHeaderMinimised?: boolean;

  logoUrl!: string;
  protected logoInvertedUrl!: string;
  protected logoTransparentUrl!: string;

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isTopNav$ = this.settings.isTopNavigation();

    if (APP_CONFIG.demoMode !== undefined) {
      this.isDemoMode = APP_CONFIG.demoMode;
    }

    this.subscriptions.push(
      this.settings.getInvertedLogo().subscribe((logoUrl: any) => {
        this.logoInvertedUrl = logoUrl;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.settings.getTransparentLogo().subscribe((logoUrl: any) => {
        this.logoTransparentUrl = logoUrl;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.settings.isTransparentNavigation().subscribe((state: any) => {
        this.isTransparentNav = state;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.settings.showPosts().subscribe((state: any) => {
        this.postsEnabled = state;
      }),
    );

    this.subscriptions.push(
      this.settings.showNotifications().subscribe((state: any) => {
        this.notificationsEnabled = state;
      }),
    );

    this.subscriptions.push(
      this.settings.showSearch().subscribe((state: any) => {
        this.searchEnabled = state;
      }),
    );

    this.subscriptions.push(
      this.settings.showHeroImages().subscribe((state: any) => {
        this.heroImagesEnabled = state;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.settings.showArticleThumbnails().subscribe((state: any) => {
        this.postsInNavEnabled = state;
      }),
    );

    this.subscriptions.push(
      this.settings.isDarkMode().subscribe((state: any) => {
        this.darkMode = state;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.settings.isDarkModeEnabled().subscribe((state: any) => {
        if (state === false && this.darkMode) {
          this.settings.toggle('isDarkMode');
        }
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.pages.currentPage$!.subscribe((page: PageType) => {
        this.page = page;
        this.post = undefined;
        this.updateLogoUrl();
      }),
    );

    this.subscriptions.push(
      this.posts.currentPost$!.subscribe((post: PostType) => {
        this.post = post;
        this.page = undefined;
        this.updateLogoUrl();
      }),
    );

    this.resetChildNav();
    this.hamburger = false;
    this.hamburgerChildNav = false;
    this.user = this.auth.getUser();
    this.isLoggedIn = this.auth.isAuthenticated();
    this.client.getAccessType().subscribe((type: any) => {
      this.authenticationType = type;
    });

    this.breakpoint.observe(['(max-width: 991px)']).subscribe(result => {
      this.isMobileNav = result.matches;
    });

    this.subscriptions.push(
      this.api.zoneChangeEmit.subscribe((zone: number | false) => {
        const headers: any = {};
        if (zone) {
          headers['x-service-zone'] = String(zone);
        }
        this.pages$ = this.pages.getPages(headers).pipe(
          map((pages: PageType[]) => {
            pages.forEach((page: PageType) => {
              this.sortChildren(page.children);
              if (
                !this.openChildNav &&
                this.pages.getCurrentPage() &&
                (this.pages.getCurrentPage()?.parentId ?? 0) > 0
              ) {
                if (page.id === this.pages.getCurrentPage().parentId) {
                  this.openChildNav = page;
                }
              }
              // Checks for true 'isFlyoutMenu' meta value on each page and sets a boolean and a page id to a couple of vars. This is used to allow individual flyout menus on pages if required.
              if (page.meta && page.meta?.['isFlyoutMenu']) {
                if (page.meta?.['isFlyoutMenu'] === 'true') {
                  this.isIndividualMenuFlyout = true;
                  this.menuFlyoutPageId = page.id;
                }
              }
            });
            return pages;
          }),
        );
      }),
    );
  }

  showHeader(): boolean {
    return this.auth.isAuthenticated() || this.authenticationType === 'public';
  }

  trackByFn(idx: number) {
    return idx;
  }

  childNav(page: any) {
    this.openChildNav = page;
  }

  resetChildNav() {
    this.openChildNav = false;
  }

  logout() {
    return this.auth.logout();
  }

  sortChildren(children: PageType[]) {
    children.sort((a, b) => (a.name > b.name ? 1 : -1));
  }

  isCurrentPage(page: any) {
    return this.pages.getCurrentPage().url === page.url;
  }

  minimiseHeader(isScrollActive: boolean) {
    this.isHeaderMinimised = isScrollActive;
  }

  onScroll() {
    const offset = 100;
    const currentPosition = this.windowService.pageYOffset;
    if (currentPosition > offset) {
      this.minimiseHeader(true);
    } else {
      this.minimiseHeader(false);
    }
  }

  showInMenu(page: any, type: string, menuType?: string) {
    if (type === 'no-child') {
      return page.meta.showInMenu == 'true' || page.meta.showInMenu == "'true'" || page.meta.showInMenu == true;
    } else if (type === 'has-child') {
      return (
        (page.meta.showInMenu == 'true' || page.meta.showInMenu == "'true'" || page.meta.showInMenu == true) &&
        !page.meta.hideNavDropdown
      );
    } else {
      return false;
    }
  }

  isDemoModeAndPostsLink(page: any) {
    return this.isDemoMode && page.url === 'articles';
  }

  getSiteName() {
    return this.client.getSiteName();
  }

  updateLogoUrl(): void {
    const serviceZone = Number(this.api.getServiceZone());
    const zoneLogo = APP_CONFIG.imagery.logos?.find(logo => logo.zone === serviceZone);
    this.logoUrl = zoneLogo?.url ?? (!this.darkMode ? this.logoInvertedUrl : this.logoTransparentUrl);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  public goToMain(elementId: string) {
    const element = this.documentService.getElementById(elementId);
    element?.focus();
  }

  /**
   * Retrieves a valid image URL based on the provided page metadata.
   *
   * @param {any} page - Page object used to determine the valid image URL.
   * @return {string} - A valid image URL as a string.
   */
  validImageUrl(page: any): SafeHtml {
    return this.imageService.getImageUrl(page, 'menu');
  }
}
